import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChatStore } from '@/stores/chatStore';
import { useConversationStore } from '@/stores/conversationStore';
import { fetchConversationHistory, listenForTitleUpdates } from '@/lib/api/conversations';
import { useConversations } from '@/queries/useConversations';

export function useConversationManager() {
    const [searchParams, setSearchParams] = useSearchParams();
    const conversationId = searchParams.get('conversationId');
    const { refetchConversations } = useConversations();
    const { addConversation, updateConversationTitle: updateStoreConversationTitle } = useConversationStore();
    const isInternalUrlUpdate = useRef(false);

    const { currentConversationId, setCurrentConversationId, setMessages, setMessagesLoading } = useChatStore();

    // Load conversation history when navigating to existing conversation
    useEffect(() => {
        if (conversationId && !isInternalUrlUpdate.current) {
            // Only fetch history if this is a real navigation (not our internal URL update)
            setCurrentConversationId(conversationId);
            setMessagesLoading(true);
            fetchConversationHistory(conversationId)
                .then(chatResponses => {
                    setMessages(chatResponses);
                })
                .catch(error => {
                    console.error('Error fetching conversation history:', error);
                })
                .finally(() => setMessagesLoading(false));
        }

        // Reset the flag after the effect runs
        isInternalUrlUpdate.current = false;
    }, [conversationId, setCurrentConversationId, setMessages, setMessagesLoading]);

    // Function to update URL with new conversation ID and start 3-stage conversation flow
    const updateConversationUrl = (newConversationId: string) => {
        isInternalUrlUpdate.current = true;
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newConversationId);
            return newParams;
        });
        setCurrentConversationId(newConversationId);

        // Stage 1: Add pending conversation with animated ellipsis
        addConversation({
            id: newConversationId,
            title: '...',
            isPending: true,
            message_count: 1,
        });

        // Stage 2: Listen for SSE title update
        listenForTitleUpdates(newConversationId, {
            onTitleUpdate: (conversationId, title) => {
                // Update title immediately in store
                updateStoreConversationTitle(conversationId, title);

                // Stage 3: Background refetch for full data (debounced)
                refetchConversations();
            },
        });
    };

    return {
        conversationId,
        currentConversationId,
        updateConversationUrl,
    };
}
